import { getAxisTicks } from 'svelte-plots-basic/utils';

// default plot settings
export const MARGINS = [0.85, 1.1, 0.45, 0.4];

// default colors for SIMCA categories
export const CAT_COLORS = {
   'regular': '#2679B2',
   'extreme': '#F2B825',
   'outlier': '#D22C2F', 'outliers': '#D22C2F',
   'alien': '#2679B2', 'aliens': '#2679B2',
   'external': '#D22C2F'
};

// default colors for series
export const LINE_COLORS = {'cal': '#2679B2', 'pv': '#D22C2F', 'target': '#2679B2', 'alt': '#2679B2'};
export const FACE_COLORS = {'cal': '#f0f8ff', 'pv': '#fff8f0', 'target': '#f0f8ff', 'alt': '#f0f8ff'};
export const LABELS_COLOR = '#a0a0a0';
export const LABELS_COLOR_SELECTED = '#909090';
export const AXIS_COLOR = '#909090';
export const MAX_LABEL_SYMBOLS = 8;

// default parameters to save plots as PNG file
export const PNG_WIDTH = 8;   // cm
export const PNG_HEIGHT = 8;  // cm
export const PNG_RES = 600;   // pixels per inch

/**
 * Returns array with colors for given number of categories.
 * @param {number} n - number of categories (between 1 nd 16).
 * @returns {Array}
 */
export function getColors(n) {
   if (n ==  1) return ["#2679B2"];
   if (n ==  2) return ["#2679B2", "#D22C2F"];
   if (n ==  3) return ["#2679B2", "#92B42A", "#D22C2F"];
   if (n ==  4) return ["#2679B2", "#2E9658", "#F2B825", "#D22C2F"];
   if (n ==  5) return ["#2679B2", "#22988A", "#92B42A", "#F79426", "#D22C2F"];
   if (n ==  6) return ["#2679B2", "#1C9AA8", "#379531", "#EED524", "#FB7F28", "#D22C2F"];
   if (n ==  7) return ["#2679B2", "#1D94A9", "#2E9658", "#92B42A", "#F2B825", "#F47129", "#D22C2F"];
   if (n ==  8) return ["#2679B2", "#1E90AA", "#279775", "#519E2F", "#D3CB25", "#F5A326", "#EF672A", "#D22C2F"];
   if (n ==  9) return ["#2679B2", "#1F8DAB", "#22988A", "#33953F", "#92B42A", "#EFCA24", "#F79426", "#EB5F2A", "#D22C2F"];
   if (n == 10) return ["#2679B2", "#208BAC", "#1E999A", "#2E9658", "#5FA32E", "#C5C626", "#F2B825", "#F98827", "#E85A2B", "#D22C2F"];
   if (n == 11) return ["#2679B2", "#2089AD", "#1C9AA8", "#29976C", "#379531", "#92B42A", "#EED524", "#F4AA26", "#FB7F28",
      "#E6552B", "#D22C2F"];
   if (n == 12) return ["#2679B2", "#2188AD", "#1C97A8", "#25987C", "#329546", "#68A62D", "#BCC327", "#F0C524", "#F69E26",
      "#F77728", "#E4512B", "#D22C2F"];
   if (n == 13) return ["#2679B2", "#2186AD", "#1D94A9", "#22988A", "#2E9658", "#469A2F", "#92B42A", "#DECF25", "#F2B825",
      "#F79426", "#F47129", "#E34E2C", "#D22C2F"];
   if (n == 14) return ["#2679B2", "#2285AE", "#1E92AA", "#209995", "#2A9767", "#34953A", "#6FA82D", "#B5C128", "#EFCE24", "#F3AD25",
      "#F98C27", "#F16B29", "#E14B2C", "#D22C2F"];
   if (n == 15) return ["#2679B2", "#2284AE", "#1E90AA", "#1D999F", "#279775", "#31964A", "#519E2F", "#92B42A", "#D3CB25", "#F0C224",
      "#F5A326", "#FA8527", "#EF672A", "#E0492C", "#D22C2F"];

   // if n > 15 return 16 colors
   return ["#2679B2", "#2283AE", "#1F8FAB", "#1C9AA8", "#249880", "#2E9658", "#379531", "#73AA2C", "#B0BF28", "#EDD424", "#F2B825",
      "#F69B26", "#FB7F28", "#ED632A", "#DF472C", "#D22C2F"];
}



/**
 * Generates axis title for given component and result object.
 * @param resobj - result object (single)
 * @param ncomp - component index (from 1)
 */
export function getAxisTitle(resobj, ncomp) {
   return `PC${ncomp} (${(resobj.stat.expvar[ncomp - 1] * 100).toFixed(1)}%)`;
}



/**
 * Take a subset of values without outliers.
 * @param {TypedArray} x - vector with selected variable values.
 * @param {Array} samples - row indices of samples without excluded outliers
 */
export function subset(x, samples) {
   if (x.length === samples.length) return x;
   const n = samples.length;
   const out = new x.constructor(n);
   for (let i in samples) {
      out[i] = x[samples[i]];
   }
   return out;
}


/**
 * Splits data values into n intervals and return a vector with interval boundaries.
 * @param {TypedArray} x -
 * @param n
 */
export function split(x, n) {
   let xMax = Number.NEGATIVE_INFINITY;
   let xMin = Number.POSITIVE_INFINITY;

   // compute min and max
   for (let i = 0; i < x.length; i++) {
      xMin = Math.min(xMin, x[i]);
      xMax = Math.max(xMax, x[i]);
   }

   // get nice values for colorbar ticks
   let ticks = getAxisTicks(undefined, [xMin, xMax], n - 1, true, false, 0).v;

   // add extra on left and right if ticks do not cover the whol range
   const dt = ticks[1] - ticks[0];
   if (ticks[0] > xMin) {
      const left = ticks[0] - dt;
      ticks = [left < xMin ? left : xMin, ...ticks];
   }
   if (ticks[ticks.length - 1] < xMax) {
      const right = ticks[ticks.length - 1] + dt;
      ticks = [...ticks, right > xMax ? right : xMax];
   }

   return ticks;
}


/**
 * Returns JSON with color for each class category
 * @param {Array} classNames - array with unique class names
 * @returns {Object}
 */
export function getClassColors(classNames) {
   const n = classNames.length;
   if (n < 1 || n > 16) {
      throw Error("getClassColors: can not create color map for more than 16 categories.");
   }

   const colors = getColors(n);
   const out = classNames.reduce((acc, curr, index) => {
      acc[curr] = colors[index];
      return acc;
   }, {});
   return out;
}


/**
 * Generates a vector of colors for each value in vector "x" depending on which interval its value falls into.
 * @param {TypedArray} x - vector with variable values (outliers free).
 * @param {Array} breaks - array with boundaries of the intervals.
 * @param {Array} colmap - array with color for each interval.
 *
 * @returns {Array} - same size as x, but with colors.
 */
export function getObjColors(x, breaks, colmap) {
   const n = breaks.length;

   // add a bit extra to right side of the last bin
   const colors = new Array(x.length);
   const delta = breaks[1] - breaks[0];
   // process the values
   for (let i = 0; i < x.length; i++) {
      for (let j = 0; j < n - 1; j++) {
         const left = x[i] >= breaks[j];
         const right = j === n - 2 ? x[i] < breaks[j + 1] + delta: x[i] < breaks[j + 1];
         if (left && right) {
            colors[i] = colmap[j];
            break;
         }
      }
   }

   return colors;
}


export function getColorGroups(dataset, res, colorBy, colorByVar, resID = 'cal') {

   if (!res) return [null, null, null, null, null];

   let collabels, colmap, colbreaks, objcolors, colorVarName;
   if (colorBy === 'groups' && res.classNames) {
      // color by groups
      const classColmap = getClassColors(res.classNames);
      colmap = Object.keys(classColmap).map(v => classColmap[v]);
      collabels = res.classNames;
      objcolors = res.classes.map(v => classColmap[v]);
      colbreaks = collabels.map((v, i) => i);
   } else if (colorBy === 'variable') {
      // color by variable
      const colorVar = subset(dataset.values.getcolref(colorByVar), res.objindices);
      colorVarName = dataset.varlabels[colorByVar - 1];
      if (colorVarName.length > 30) {
         colorVarName = colorVarName.substring(0, 29) + '...'; // limit varname by 30-32 symbols to fit the plot width
      }
      colbreaks = split(colorVar, 10);
      colmap = getColors(colbreaks.length - 1);
      objcolors = getObjColors(colorVar, colbreaks, colmap);
   } else {
      // no color grouping
      collabels = null;
      colbreaks = null;
      colmap = null;
      objcolors = Array(res.nrows).fill(LINE_COLORS[resID]);
   }

   return [collabels, colmap, colbreaks, objcolors, colorVarName];
}

/**
 * Returns JSON with x and y coordinates, legend item, colors and axis limits for a particular
 * statistic (e.g. sensitivity, specificity, etc) of result object.
 *
 * @param {Object} resobj - JSON with one or several result objects (SIMCA, PCA, etc.).
 * @param {string} id - ID of the object to compute the series values for (e.g. "cal" or "pv").
 * @param {string} statName - name of statistic to extract (e.g. "sens").
 * @returns {Object}
 */
export function getSeries(resobj, id, statName) {

   if (!resobj || !id) return null;

   if (!Object.keys(resobj).includes(id)) {
      throw Error('getSeries: the result object has no element with ID: "' + id + '"');
   }

   const res = resobj[id];

   const ncomp = res.v.length;
   const y = Array(ncomp);
   const x = Array(ncomp);
   let yMin = 9999999999;
   let yMax = -999999999;

   for (let a = 0; a < ncomp; a++) {
      const ya = res.v[a].stat[statName];
      x[a] = a + 1;
      y[a] = ya;
      yMin = Math.min(yMin, ya);
      yMax = Math.max(yMax, ya);
   }

   const legendItem = {label: id, marker: 1, borderColor: LINE_COLORS[id], faceColor: FACE_COLORS[id]};
   const lineColor = LINE_COLORS[id];
   const faceColor = FACE_COLORS[id];
   return {x, y, legendItem, lineColor, faceColor, yMin, yMax};
}